/**
 * 模板选择器组件
 * 用于选择文档模板
 */

import React, { useState, useEffect } from 'react';
import { Modal, Card, Input, Tabs, Space, Button,  Spin  } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import { 
  SearchOutlined, 
  FileTextOutlined, 
  BookOutlined, 
  ProjectOutlined,
  FileMarkdownOutlined,
  LoadingOutlined,
  FileAddOutlined
} from '@ant-design/icons';
import { useTemplateSelectorStyles } from '../../styles/components/markdown/TemplateSelector';
import markdownService from '../../services/markdownService';

// 导入Markdown模板文件
import prdTemplate from '../../assets/产品需求文档.md';
import techSpecTemplate from '../../assets/技术方案说明书.md';
import gtmTemplate from '../../assets/上线与增长计划.md';
import iterationTemplate from '../../assets/产品迭代与优化日志.md';
import oceanMarketResearchTemplate from '../../assets/海外市场调研报告.md';

const { Search } = Input;
const { TabPane } = Tabs;

const TemplateSelector = ({ visible, onClose, onSelect }) => {
  const { styles } = useTemplateSelectorStyles();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  // 获取模板列表
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // 使用导入的模板内容
      const frontendTemplates = [
        {
          id: 'template-empty',
          name: '空模板',
          description: '创建一个空白文档，没有任何预填充内容',
          content: '',
          type: 'empty',
          category: 'empty',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        },
        {
          id: 'template-ocean-market-research',
          name: '海外市场调研报告',
          description: '告诉AI"我们的海外市场调研报告"。',
          content: oceanMarketResearchTemplate,
          type: 'market-research',
          category: 'market-research',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        },
        {
          id: 'template-prd',
          name: '产品需求文档',
          description: '告诉AI"我们要做什么(What)"、"为谁做(Who)"以及"如何衡量成功(Measurement)"。',
          content: prdTemplate,
          type: 'requirement',
          category: 'requirement',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        },
        {
          id: 'template-tech-spec',
          name: '技术方案说明书',
          description: '告诉AI"我们要如何构建(How)"。这是AI生成代码最直接的依据。',
          content: techSpecTemplate,
          type: 'technical',
          category: 'technical',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        },
        {
          id: 'template-gtm',
          name: '上线与增长计划',
          description: '告诉AI"我们的自我定位"以及"获客与推广策略 (Acquisition & Marketing Strategy)"。',
          content: gtmTemplate,
          type: 'project',
          category: 'project',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        },
       
        {
          id: 'template-iteration',
          name: '产品迭代与优化日志',
          description: '清晰地记录和呈现团队在每一轮迭代中的"反馈 -> 思考 -> 行动 -> 结果"的完整闭环，作为复赛阶段"优化提升率"维度的核心评审依据。',
          content: iterationTemplate,
          type: 'iteration',
          category: 'iteration',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        }
      ];
      
      setTemplates(frontendTemplates);
      setFilteredTemplates(frontendTemplates);
    } catch (error) {
      window.message?.error('获取模板列表失败');
      console.error('获取模板列表失败:', error);
      
      // 即使获取失败，也要显示默认模板
      const defaultTemplates = [
        {
          id: 'template-empty',
          name: '空模板',
          description: '创建一个空白文档，没有任何预填充内容',
          content: '',
          type: 'empty',
          category: 'empty',
          inserted_at: new Date().toISOString(),
          usage_count: 0
        }
      ];
      
      setTemplates(defaultTemplates);
      setFilteredTemplates(frontendTemplates);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchTemplates();
    }
  }, [visible]);

  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = templates.filter(template => 
      template.name.toLowerCase().includes(value.toLowerCase()) ||
      template.description.toLowerCase().includes(value.toLowerCase()) ||
      (template.type && template.type.toLowerCase().includes(value.toLowerCase())) ||
      (template.category && template.category.toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredTemplates(filtered);
  };

  // 处理模板选择
  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
  };

  // 处理确认选择
  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
    } else {
      window.message?.warning('请选择一个模板');
    }
  };

  // 处理取消
  const handleCancel = () => {
    setSelectedTemplate(null);
    setSearchText('');
    setActiveTab('all');
    onClose();
  };

  // 获取模板图标
  const getTemplateIcon = (type) => {
    switch (type) {
      case 'empty':
        return <FileAddOutlined className={styles.templateIcon} />;
      case 'technical':
        return <FileTextOutlined className={styles.templateIcon} />;
      case 'project':
        return <ProjectOutlined className={styles.templateIcon} />;
      case 'requirement':
        return <FileMarkdownOutlined className={styles.templateIcon} />;
      case 'iteration':
        return <BookOutlined className={styles.templateIcon} />;
      default:
        return <BookOutlined className={styles.templateIcon} />;
    }
  };

  // 获取模板类型标签
  const getTemplateTypeLabel = (type) => {
    switch (type) {
      case 'empty':
        return '空白文档';
      case 'technical':
        return '技术文档';
      case 'project':
        return '项目计划';
      case 'requirement':
        return '需求分析';
      case 'iteration':
        return '迭代日志';
      default:
        return '其他';
    }
  };

  // 按分类过滤模板
  const getTemplatesByCategory = (category) => {
    if (category === 'all') {
      return filteredTemplates;
    }
    return filteredTemplates.filter(template => template.category === category);
  };

  // 渲染模板卡片
  const renderTemplateCard = (template) => (
    <Card
      key={template.id}
      className={`${styles.templateCard} ${
        selectedTemplate?.id === template.id ? styles.templateCardSelected : ''
      }`}
      onClick={() => handleTemplateClick(template)}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.templateHeader}>
        {getTemplateIcon(template.type)}
        <div style={{ flex: 1 }}>
          <div className={styles.templateTitle}>{template.name}</div>
          <div className={styles.templateDescription}>
            {template.description}
          </div>
        </div>
      </div>
      
      <div className={styles.templateContent}>
        <div className={styles.templatePreview}>
          {template.content ? (
            template.content.length > 150 ? 
              `${template.content.substring(0, 150)}...` : 
              template.content
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>
              空白文档，从零开始创建您的内容
            </span>
          )}
        </div>
      </div>
      
      <div className={styles.templateMeta}>
        <span className={styles.templateType}>
          {getTemplateTypeLabel(template.type)}
        </span>
        <span className={styles.templateUsage}>
          使用 {template.usage_count || 0} 次
        </span>
      </div>
    </Card>
  );

  return (
    <Modal
      title="选择模板"
      open={visible}
      onCancel={handleCancel}
      width={900}
      className={styles.templateModal}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!selectedTemplate}
          className="!text-black"
        >
          确认选择
        </Button>,
      ]}
    >
      <Search
        placeholder="搜索模板..."
        className={styles.searchBar}
        onSearch={handleSearch}
        allowClear
        prefix={<SearchOutlined />}
      />

      {loading ? (
        <div className={styles.loadingContainer}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          <span style={{ marginLeft: 8 }}>加载模板中...</span>
        </div>
      ) : (
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          className={styles.categoryTabs}
        >
          <TabPane tab="全部" key="all">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('all').map(renderTemplateCard)}
            </div>
          </TabPane>
          <TabPane tab="空白文档" key="empty">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('empty').map(renderTemplateCard)}
            </div>
          </TabPane>
          <TabPane tab="需求分析" key="requirement">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('requirement').map(renderTemplateCard)}
            </div>
          </TabPane>
          <TabPane tab="技术文档" key="technical">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('technical').map(renderTemplateCard)}
            </div>
          </TabPane>
          <TabPane tab="项目计划" key="project">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('project').map(renderTemplateCard)}
            </div>
          </TabPane>
          <TabPane tab="迭代日志" key="iteration">
            <div className={styles.templateGrid}>
              {getTemplatesByCategory('iteration').map(renderTemplateCard)}
            </div>
          </TabPane>
        </Tabs>
      )}

      {filteredTemplates.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <FileAddOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <p>暂无模板</p>
          <p>请尝试其他搜索条件或联系管理员添加模板</p>
        </div>
      )}
    </Modal>
  );
};

export default TemplateSelector;

